import { Component, ViewChild } from '@angular/core';
import { BaseComponent } from '../base/base.component';
import { FragmentService } from '../../services/fragment.service';
import { LoaderService } from '../../services/loader.service';
import { MetaService } from '../../services/meta.service';
import { MenuService } from 'app/services/menu.service';
import { ConfigService } from '../../services/config.service';
import { BreakpointService } from 'app/modules/breakpoint/breakpoint.service';
import { BehaviorSubject, filter, firstValueFrom, forkJoin, of, take } from 'rxjs';
import { CategoryNode, DropdownWithCount, MenuItem, ProductDetail } from '../../models/allProduct.interface';
import { MultiSelectDropdownComponent } from '../../components/_common/multi-select-dropdown/multi-select-dropdown.component';

@Component({
  selector: 'app-all-product',
  templateUrl: './all-product.component.html',
  standalone: false,
})
export class AllProductComponent extends BaseComponent {
  isSidebarPresent = false;
  loading = true;
  dataLoaded = false;
  isSearchEnabled = true;
  showTags: true;
  showRegions: true;
  menuItems: MenuItem[] = [];
  categoryData: MenuItem | null = null;
  categoryDataWithCounts: MenuItem | null = null;
  hierarchicalCategoryTree: CategoryNode | null = null;
  selectedCategories = new Set<string>();
  regionPlaceholder = 'Region';
  businessTypePlaceholder = 'Business Type';
  categoryPlaceholder = 'Category';
  productTypePlaceholder = 'Product Type';
  searchTerm = '';
  isAnyFilterApplied = false;
  regionDropdown$ = new BehaviorSubject<DropdownWithCount[]>([]);
  businessTypeDropdown$ = new BehaviorSubject<DropdownWithCount[]>([]);
  productTypeDropdown$ = new BehaviorSubject<DropdownWithCount[]>([]);
  isResponsiveApplicable = false;
  productDetails: ProductDetail[] = [];
  _categoryScopedProducts: ProductDetail[] | null = null;

  private firstSelectedRegion: string | null = null;
  private firstSelectedBusinessType: string | null = null;
  private firstSelectedProductType: string | null = null;
  private firstSelectedCategory: string | null = null;
  private selectedRegions = new Set<string>();
  private selectedBusinessTypes = new Set<string>();
  private selectedProductType = new Set<string>();

  @ViewChild('hierarchyCategoryDropdown') hierarchyCategoryDropdown?: MultiSelectDropdownComponent;

  constructor(
    public fragmentService: FragmentService,
    public loaderService: LoaderService,
    public metaService: MetaService,
    private menuService: MenuService,
    private configService: ConfigService,
    private breakpointService: BreakpointService
  ) {
    super(fragmentService, loaderService, metaService);
  }

  async ngOnInit(): Promise<void> {
    const meta = await firstValueFrom(this.metaService.metadata.pipe(filter(Boolean), take(1)));

    const sidebarInfo = meta['category-menu'] || null;
    const productInfo = meta['product-info'] || null;
    this.isSearchEnabled = meta['isSearchEnabled'] ?? true;
    this.showRegions = meta['showRegions'] ?? true;
    this.showTags = meta['showTags'] ?? true;
    this.isSidebarPresent = !!sidebarInfo;

    const menu$ = sidebarInfo ? this.menuService.getMenu(sidebarInfo) : of(null);
    const products$ = productInfo ? this.configService.getConfig(productInfo) : of(null);

    try {
      const [menuResp, productResp] = await firstValueFrom(forkJoin([menu$, products$]));
      if (menuResp) {
        this.menuItems = menuResp?.Items?.[0]?.items || [];
        this.categoryData = this.menuItems[0] || null;
      }
      if (productResp) {
        this.productDetails = productResp?.Items?.[0]?.products || [];
      }

      this.categoryDataWithCounts = this.computeCategoryCounts(this.categoryData, this.productDetails);
      this.hierarchicalCategoryTree = this.normalizeCategoryTree(this.categoryDataWithCounts);
      this.updateDropdownFilters();
      this.dataLoaded = true;
    } finally {
      this.loading = false;
    }
    this.breakpointService.getBreakpointObservable().subscribe((bp) => {
      this.isResponsiveApplicable = ['xs', 'sm', 'md', undefined].includes(bp);
      if (!this.isResponsiveApplicable) {
        if (this.selectedCategories.size > 0) {
          this.selectedCategories.clear();
          this.applyFilters();
        }
      }
    });
  }

  private computeCategoryCounts(root: MenuItem | null, products: ProductDetail[]): MenuItem | null {
    if (!root) return null;

    const familyMap = new Map<string, Set<number>>();
    products.forEach((p, idx) => {
      this.splitFamilyNames(p.familyName).forEach((name) => {
        if (!familyMap.has(name)) familyMap.set(name, new Set<number>());
        familyMap.get(name)!.add(idx);
      });
    });

    const cloneWithUniqueCounts = (node: MenuItem): MenuItem => {
      const clonedChildren = node.children?.map(cloneWithUniqueCounts) || [];
      const ownSet =
        node.title === 'All Categories'
          ? new Set<number>(Array.from({ length: products.length }, (_, i) => i))
          : new Set<number>(familyMap.get(node.title) || []);

      const unionSet = new Set<number>(ownSet);
      clonedChildren.forEach((child) => {
        (child as any)._productSet?.forEach((i: number) => unionSet.add(i));
      });

      const totalCount = unionSet.size;

      const cloned: MenuItem = {
        title:
          node.title === 'Categories'
            ? 'Categories'
            : `${node.title} <span class="-count-category">(${totalCount})</span>`,
        count: totalCount,
        children: clonedChildren.length ? clonedChildren : undefined,
      };
      (cloned as any)._productSet = unionSet;
      return cloned;
    };

    return cloneWithUniqueCounts(root);
  }

  private normalizeCategoryTree(root: MenuItem | null): CategoryNode | null {
    if (!root) return null;

    const stripRaw = (title: string): string => {
      return title
        .replace(/<span[^>]*>\(\d+\)<\/span>/gi, '')
        .replace(/\(\d+\)$/g, '')
        .trim();
    };

    const build = (node: MenuItem): CategoryNode => {
      const raw = stripRaw(node.title);
      const isRoot = raw === 'Categories';

      const filteredChildren = (node.children || [])
        .filter(child => !stripRaw(child.title).startsWith('All Categories'))
        .map(build);

      return {
        id: isRoot ? '__root_categories__' : raw,
        labelHtml: isRoot ? '__root_categories__' : raw,
        children: filteredChildren,
      };
    };

    return build(root);
  }

  private buildRegionCounts(items: ProductDetail[]): DropdownWithCount[] {
    const counts = new Map<string, number>();
    items.forEach((p) => this.splitRegions(p.region).forEach((r) => counts.set(r, (counts.get(r) || 0) + 1)));
    return Array.from(counts.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  private buildProductTypeCounts(items: ProductDetail[]): DropdownWithCount[] {
    const counts = new Map<string, number>();
    items.forEach((p) => this.splitProductTypes(p.productType).forEach((t) => counts.set(t, (counts.get(t) || 0) + 1)));
    return Array.from(counts.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  private buildTagCounts(items: ProductDetail[]): DropdownWithCount[] {
    const counts = new Map<string, number>();
    items.forEach((p) =>
      (p.tags || []).forEach((t) => {
        if (!t) return;
        counts.set(t, (counts.get(t) || 0) + 1);
      })
    );
    return Array.from(counts.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  updateDropdownFilters(): void {
    this.businessTypeDropdown$.next(this.buildTagCounts(this.productDetails));
    this.regionDropdown$.next(this.buildRegionCounts(this.productDetails));
    this.productTypeDropdown$.next(this.buildProductTypeCounts(this.productDetails));
  }

  splitRegions(region?: string): string[] {
    return region
      ? region
          .split(',')
          .map((r) => r.trim())
          .filter(Boolean)
      : [];
  }

  private splitProductTypes(productType?: string | string[]): string[] {
    if (!productType) return [];
    if (Array.isArray(productType)) {
      return productType.map((t) => (t || '').trim()).filter(Boolean);
    }
    return productType
      .split(',')
      .map((t) => t.trim())
      .filter((t) => t.length > 0);
  }

  private splitFamilyNames(familyName?: string): string[] {
    return familyName
      ? familyName
          .split(',')
          .map((n) => n.trim())
          .filter((n) => n.length > 0)
      : [];
  }

  countryIconName(country: string): string {
    return country.toLowerCase().replace(/\s+/g, '-');
  }

  trackByProductIdentity(index: number, product: ProductDetail): string {
    return (product.productTitle || '') + '|' + (product.productTo || index);
  }

  private normalizeSearchTokens(raw: string): string[] {
    return raw
      .trim()
      .toLowerCase()
      .split(/\s+/)
      .filter((t) => t.length > 0);
  }

  private matchesSearch(p: ProductDetail, tokens: string[]): boolean {
    if (!tokens.length) return true;
    const haystack = `${p.productTitle || ''} ${p.productDescription || ''}`.toLowerCase();
    return tokens.every((t) => haystack.includes(t));
  }

  onSearchChange(event?: Event): void {
    this.searchTerm = event ? (event.target as HTMLInputElement).value : '';
    this.applyFilters();
  }

  onCategorySelection(item: MenuItem): void {

    this.breakpointService.closeOtherDropdowns('');

    const self = this as any;
    if (!self._allProducts) {
      self._allProducts = [...this.productDetails];
    }

    const strip = (t: string): string => {
      if (!t) return '';
      return t
        .replace(/<span[^>]*>\(\d+\)<\/span>/gi, '')
        .replace(/\(\d+\)$/g, '')
        .trim();
    };

    const selectedName = strip(item.title);

    this.onClearFilter();

    if (selectedName === 'All Categories' || selectedName === 'Categories') {
      this._categoryScopedProducts = null;
    } else {
      const collected = new Set<string>();
      const collect = (node: MenuItem | undefined) => {
        if (!node) return;
        const name = strip(node.title);
        if (name && name !== 'Categories') collected.add(name);
        node.children?.forEach(collect);
      };
      collect(item);

      this._categoryScopedProducts = self._allProducts.filter((p: ProductDetail) =>
        this.splitFamilyNames(p.familyName).some((f) => collected.has(f))
      );
    }

    this.applyFilters();
    this.updateDropdownFilters();
  }

  onHierarchicalCategoriesChange(selected: Set<string>): void {
    if (selected.size === 0) {
      this.selectedCategories = new Set<string>();
      this.firstSelectedCategory = null;
      this._categoryScopedProducts = null;
    } else {
      this.selectedCategories = selected;
      this.firstSelectedCategory = Array.from(selected)[0] || null;
    }
    this.applyFilters();
    this.updateSelectionPlaceholders();
  }

  onHierarchicalCategoriesCleared(): void {
    this.selectedCategories = new Set<string>();
    this.firstSelectedCategory = null;
    this._categoryScopedProducts = null;
    this.applyFilters();
    this.updateSelectionPlaceholders();
  }

  onDropdownSelected(type: 'region' | 'businessType' | 'productType', selectedOption: DropdownWithCount): void {
    if (!selectedOption) return;

    const targetSet =
      type === 'region' ? this.selectedRegions : type === 'businessType' ? this.selectedBusinessTypes : this.selectedProductType;
    const firstRef =
      type === 'region' ? 'firstSelectedRegion' : type === 'businessType' ? 'firstSelectedBusinessType' : 'firstSelectedProductType';

    if ((selectedOption as any).clearAll) {
      targetSet.clear();
      (this as any)[firstRef] = null;
      this.applyFilters();
      this.updateSelectionPlaceholders();
      return;
    }

    if (selectedOption.checked) {
      targetSet.add(selectedOption.name!);
      if (targetSet.size === 1) {
        (this as any)[firstRef] = selectedOption.name;
      }
    } else {
      const wasFirst = (this as any)[firstRef] === selectedOption.name;
      targetSet.delete(selectedOption.name!);
      if (wasFirst) {
        const next = targetSet.values().next();
        (this as any)[firstRef] = next.done ? null : next.value;
      }
    }

    this.applyFilters();
    this.updateSelectionPlaceholders();
  }

  onClearFilter(): void {
    if (this.hierarchyCategoryDropdown) {
      this.hierarchyCategoryDropdown.clearHierarchical();
    } else {
      this.onHierarchicalCategoriesCleared();
    }
    this.selectedBusinessTypes.clear();
    this.selectedRegions.clear();
    this.selectedProductType.clear();
    this.firstSelectedBusinessType = null;
    this.firstSelectedRegion = null;
    this.firstSelectedProductType = null;
    this._categoryScopedProducts = null;
    
    const titleActiveElements = document.querySelectorAll('.title.active');
    titleActiveElements.forEach(el => {
      el.className = el.className.replace('title active', 'title');
    });
    
    const itemRowActiveElements = document.querySelectorAll('.item-row.clickable.active');
    itemRowActiveElements.forEach(el => {
      el.className = el.className.replace('item-row clickable active', 'item-row clickable');
    });
    
    this.applyFilters();
    this.updateSelectionPlaceholders();
    this.onDropdownSelected('businessType', { name: '', count: 0, checked: false, clearAll: true } as any);
    this.onDropdownSelected('region', { name: '', count: 0, checked: false, clearAll: true } as any);
    this.onDropdownSelected('productType', { name: '', count: 0, checked: false, clearAll: true } as any);
  }

  private updateSelectionPlaceholders(): void {
    const buildLabel = (set: Set<string>, defaultLabel: string, first: string | null): string => {
      const size = set.size;
      if (size === 0) return defaultLabel;
      const primary = first ?? Array.from(set)[0];
      return size === 1 ? primary : `${primary} + ${size - 1}`;
    };

    this.regionPlaceholder = buildLabel(this.selectedRegions, 'Region', this.firstSelectedRegion);
    this.productTypePlaceholder = buildLabel(this.selectedProductType, 'Product Type', this.firstSelectedProductType);
    this.businessTypePlaceholder = buildLabel(
      this.selectedBusinessTypes,
      'Business Type',
      this.firstSelectedBusinessType
    );
    this.categoryPlaceholder = buildLabel(this.selectedCategories, 'Categories', this.firstSelectedCategory);
  }

  private applyFilters(): void {
    const self = this as any;
    if (!self._allProducts) {
      self._allProducts = [...this.productDetails];
    }

    let base = this._categoryScopedProducts ?? self._allProducts;

    const hasMultiCategory = this.isResponsiveApplicable && this.selectedCategories.size > 0;
    if (hasMultiCategory) {
      base = self._allProducts.filter((p: ProductDetail) =>
        this.splitFamilyNames(p.familyName).some((f) => this.selectedCategories.has(f))
      );
    }

    const hasRegion = this.selectedRegions.size > 0;
    const hasBusinessType = this.selectedBusinessTypes.size > 0;
    const hasProductType = this.selectedProductType.size > 0;
    const searchTokens = this.normalizeSearchTokens(this.searchTerm);
    const hasSearch = searchTokens.length > 0;

    this.isAnyFilterApplied = hasRegion || hasBusinessType || hasProductType || hasMultiCategory;

    if (!this.isAnyFilterApplied && !hasSearch) {
      this.productDetails = [...base];
      this.updateDropdownFilters();
      return;
    }

    const filtered = base.filter((p: ProductDetail) => {
      const regionMatch = !hasRegion || this.splitRegions(p.region).some((r) => this.selectedRegions.has(r));
      const businessTypeMatch = !hasBusinessType || (p.tags || []).some((t) => this.selectedBusinessTypes.has(t));
      const productTypeMatch = !hasProductType || this.splitProductTypes(p.productType).some((t) => this.selectedProductType.has(t));
      const searchMatch = !hasSearch || this.matchesSearch(p, searchTokens);
      return regionMatch && businessTypeMatch && productTypeMatch && searchMatch;
    });

    this.productDetails = filtered;

    if (!hasRegion && !hasBusinessType && !hasProductType && !hasMultiCategory && hasSearch) {
      this.businessTypeDropdown$.next(this.buildTagCounts(filtered));
      this.regionDropdown$.next(this.buildRegionCounts(filtered));
      this.productTypeDropdown$.next(this.buildProductTypeCounts(filtered));
    }
  }
}